import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { NgClass } from '@angular/common';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { filter, map, startWith } from 'rxjs';

import { LoadingService } from './core/services/loading.service';
import { APP_ENVIRONMENT } from './core/utils/environment.token';
import { ToastContainerComponent } from './shared/components/toast-container/toast-container.component';
import { MatIconComponent } from './shared/components/mat-icon/mat-icon.component';
import { AuthService } from './core/services/auth.service';
import { ToastService } from './core/services/toast.service';
import { AlertNotificationsService } from './core/services/alert-notifications.service';
import { TranslationPipe } from './i18n/translation.pipe';
import { TranslationService } from './i18n/translation.service';
import { SUPPORTED_LANGUAGES } from './i18n/languages';
import { LanguageSelectorComponent } from './i18n/language-selector/language-selector.component';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    NgClass,
    ToastContainerComponent,
    MatIconComponent,
    TranslationPipe,
    LanguageSelectorComponent,
  ],
  templateUrl: './app.html',
  styleUrl: './app.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class App {
  private readonly loadingService = inject(LoadingService);
  private readonly environment = inject(APP_ENVIRONMENT);
  private readonly authService = inject(AuthService);
  private readonly toastService = inject(ToastService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly alertNotificationsService = inject(AlertNotificationsService);
  protected readonly translationService = inject(TranslationService);
  private readonly themeStorageKey = 'theme';

  protected readonly theme = signal<'light' | 'dark'>(
    (localStorage.getItem(this.themeStorageKey) as 'light' | 'dark') ?? 'light',
  );
  protected readonly isNavOpen = signal(false);
  protected readonly isLoading = this.loadingService.isLoading;
  protected readonly user = this.authService.user;
  protected readonly isAuthenticated = this.authService.isAuthenticated;
  protected readonly isSuperAdmin = computed(() => this.authService.hasRole(['SUPER_ADMIN']));
  protected readonly unreadNotifications = this.alertNotificationsService.unreadCount;
  protected readonly userMenuOpen = signal(false);
  protected readonly supportedLanguages = SUPPORTED_LANGUAGES;
  protected readonly language = this.translationService.language;
  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects),
      startWith(this.router.url),
    ),
    { initialValue: this.router.url },
  );
  protected readonly isAuthRoute = computed(() => {
    const url = this.currentUrl();
    return (
      url.startsWith('/auth/') ||
      url.startsWith('/reset-password') ||
      url.startsWith('/magic-login')
    );
  });
  protected readonly isMaintenanceRoute = computed(() =>
    this.currentUrl().startsWith('/maintenance'),
  );

  protected readonly appVersion = this.environment.appVersion;
  protected readonly environmentLabel = computed(() =>
    this.translationService.translate(`app.environmentLabel.${this.environment.name}`),
  );
  protected readonly currentYear = new Date().getFullYear();
  protected readonly appOwner = computed(() => this.translationService.translate('app.owner'));
  protected readonly navigationId = 'app-primary-navigation';

  protected readonly links = computed(() => {
    const items: { labelKey: string; path: string; icon: 'home' | 'map' | 'settings' }[] = [
      { labelKey: 'navigation.home', path: '/home', icon: 'home' },
      { labelKey: 'navigation.map', path: '/stations', icon: 'map' },
    ];

    if (this.isSuperAdmin()) {
      items.push({
        labelKey: 'navigation.settings',
        path: '/admin/configuracion',
        icon: 'settings',
      });
    }

    return items;
  });

  private readonly authWatcher = effect(() => {
    if (this.isAuthenticated()) {
      this.loadUnreadNotifications();
    } else {
      this.alertNotificationsService.reset();
    }
  });

  toggleTheme(): void {
    this.theme.update((current) => {
      const nextTheme = current === 'light' ? 'dark' : 'light';
      localStorage.setItem(this.themeStorageKey, nextTheme);
      return nextTheme;
    });
  }

  toggleNavigation(): void {
    this.isNavOpen.update((open) => !open);
  }

  closeNavigation(): void {
    this.isNavOpen.set(false);
    this.userMenuOpen.set(false);
  }

  logout(): void {
    this.authService.logout();
    this.toastService.show(this.translationService.translate('auth.logoutSuccess'), 'info');
    this.closeNavigation();
  }

  toggleUserMenu(): void {
    this.userMenuOpen.update((open) => !open);
  }

  goToAccount(): void {
    void this.router.navigate(['/account']);
    this.closeNavigation();
  }

  goToFavorites(): void {
    void this.router.navigate(['/favorites']);
    this.closeNavigation();
  }

  goToAlerts(): void {
    void this.router.navigate(['/alerts']);
    this.closeNavigation();
  }

  goToNotifications(): void {
    void this.router.navigate(['/notifications']);
    this.closeNavigation();
  }

  private loadUnreadNotifications(): void {
    this.alertNotificationsService
      .fetchAlerts({ onlyUnread: true, limit: 50 })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: () =>
          this.toastService.show(
            this.translationService.translate('errors.notificationsLoad'),
            'error',
          ),
      });
  }
}
